import React from "react";

import { LazyImage } from "components/Image/Index";

import classNames from "classnames";
import * as fromProjects from "store/Projects";

import { a } from "@react-spring/web";
import { useIntersect } from "hooks/useIntersect";
import { useAnimateInTrail } from "hooks/useAnimateInTrail";
import { initialOffset } from "components/Animations/SpringProperties/SpringProperties";

interface IProps {
  animationReady: boolean;
  bodyContent: object;
}

export const PodiumValuations: React.FC<IProps> = ({
  animationReady,
  ...props
}) => {
  const bodyContent: fromProjects.IContentBrandBoardSection =
    props.bodyContent as fromProjects.IContentBrandBoardSection;
  const sectionContent: fromProjects.IContentBrandBoardPodiumValuations =
    bodyContent.sectionContent as fromProjects.IContentBrandBoardPodiumValuations;

  const [inViewRef, inView] = useIntersect({
    startObserving: animationReady,
  });

  const [animateInTrail] = useAnimateInTrail(
    inView,
    7 + sectionContent.brandColors.length
  );

  const getFadeInStyles = (index: number, offset: number) => ({
    opacity: animateInTrail[index].active,
    transform: animateInTrail[index].active.to(
      (x) => `translateY(calc(${initialOffset(x, offset)}))`
    ),
  });

  const colorContainerStyles = (
    index: number,
    color: fromProjects.IContentBrandBoardBrandColor
  ) => ({
    ...getFadeInStyles(index, 30),
    backgroundColor: color.hexCode,
  });

  const separatorLineStyles = (index: number) => ({
    width: animateInTrail[index].active.to((x) => `calc(${x} * 100%)`),
  });

  return (
    <a.div ref={inViewRef} className="brandBoardWrapper podiumValuations">
      <a.div
        className={"largeLogomarkContainer"}
        style={getFadeInStyles(0, 30)}
      >
        <div className="imageContainer">
          <LazyImage
            width="100%"
            height="100%"
            heightRatio={sectionContent.images.darkLogomark.heightRatio}
            src={
              "/assets/images/work/" + sectionContent.images.darkLogomark.url
            }
            alt={sectionContent.images.darkLogomark.alt}
            placeholderBackgroundColor="transparent"
            fit="scaleDown"
            animateImageIn={true}
            animationType={"fadeIn"}
          />
        </div>
      </a.div>
      <div className="flex-container brandContainer">
        <div className={"brandColorsContainer"}>
          {sectionContent.brandColors.map((color, index) => (
            <a.div
              key={index}
              className={"colorContainer"}
              style={colorContainerStyles(1 + index, color)}
            >
              <div
                className={classNames("hexCode", {
                  lightBackground: color.shade === "light",
                })}
              >
                {color.hexCode}
              </div>
            </a.div>
          ))}
        </div>
        <a.div
          className={"sideLogoContainer"}
          style={colorContainerStyles(4, sectionContent.brandColors[2])}
        >
          <div className="imageContainer">
            <LazyImage
              width="100%"
              height="100%"
              heightRatio={sectionContent.images.lightLogo.heightRatio}
              src={"/assets/images/work/" + sectionContent.images.lightLogo.url}
              alt={sectionContent.images.lightLogo.alt}
              placeholderBackgroundColor="transparent"
              fit="scaleDown"
              animateImageIn={true}
              animationType={"fadeIn"}
            />
          </div>
        </a.div>
      </div>

      <a.div
        className="flex-container info-grid typography-info-grid"
        style={getFadeInStyles(5, 30)}
      >
        {sectionContent.typography.map((font, index) => {
          const animationIndex = 5 + index;

          const typographyLabelList = {
            chapazaRegular: "Chapaza Regular",
            latoRegular: "Lato Regular",
            latoLight: "Lato Light",
          };

          return (
            <a.div
              key={index}
              className={classNames("tile-wrapper")}
              style={getFadeInStyles(animationIndex, 50)}
            >
              <a.h2 style={getFadeInStyles(animationIndex + 1, 30)}>
                {typographyLabelList[sectionContent.typography[index]]}
              </a.h2>
              <div className="separatorWrapper">
                <a.div
                  className="separator"
                  style={getFadeInStyles(animationIndex + 2, 30)}
                >
                  <a.div
                    className="line"
                    style={separatorLineStyles(animationIndex + 2)}
                  />
                </a.div>
              </div>
              <a.div
                className={"typographySampleContainer"}
                style={getFadeInStyles(animationIndex + 3, 30)}
              >
                <LazyImage
                  width="100%"
                  heightRatio={75 / 500}
                  src={
                    "/assets/images/projectTypography/" +
                    sectionContent.typography[index] +
                    "Dark.png"
                  }
                  alt="Font Preview"
                  fit="scaleDown"
                  animateImageIn={true}
                  animationType={"fadeIn"}
                />
              </a.div>
            </a.div>
          );
        })}
      </a.div>
    </a.div>
  );
};
